import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import PasswordInput from "../components/PasswordInput";
import { Brain, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { warmupBackend } from "../lib/api";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/98f7c84d-f342-4447-a832-a43914caf54b/images/46f5eb5d95c612907bdb87dcd40e49acfa3df553832159bb2eca09704dadfdad.png";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");        // iter130 Phase A — optional at signup
  const [password, setPassword] = useState("");
  const [consent, setConsent] = useState(false); // iter130 Phase A — DPDP consent
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [retryNotice, setRetryNotice] = useState("");
  useEffect(() => { warmupBackend(); }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setRetryNotice("");
    // iter124 — SEC-006 bumped minimum to 8 chars. Match on the client.
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (!consent) {
      setError("Please accept the Terms of Service and Privacy Policy to continue.");
      return;
    }
    setSubmitting(true);
    const res = await register(email.trim().toLowerCase(), password, name.trim(), {
      phone: phone.trim() || undefined,
      consent: true,
      onRetry: (attempt, total) => {
        setRetryNotice(`Connection issue — retrying… (${attempt}/${total})`);
      },
    });
    setSubmitting(false);
    setRetryNotice("");
    if (res.ok) {
      toast.success(`Account created. Welcome, ${res.user.name}!`);
      nav("/dashboard");
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[var(--bt-bg)]">
      <div className="relative hidden lg:block">
        <img src={HERO_IMG} alt="BrainTRADES" className="absolute inset-0 w-full h-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/40 to-[var(--bt-bg)]" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-sm bg-[var(--bt-accent)] flex items-center justify-center">
              <Brain className="w-5 h-5 text-black" strokeWidth={2.5} />
            </span>
            <span className="font-heading text-2xl">
              Brain<span className="text-[var(--bt-accent)]">TRADES</span>
            </span>
          </div>
          <div className="space-y-5 max-w-md">
            <p className="bt-label">Start your journal</p>
            <h1 className="font-heading text-5xl leading-[1.05]">
              Every trade<br />
              tells a <span className="text-[var(--bt-accent)]">story.</span>
            </h1>
            <p className="text-[var(--bt-text-muted)] leading-relaxed">
              Tag emotions, see win-rate evolution, and stop repeating losing patterns. Beginner-friendly. Pro-ready.
            </p>
          </div>
          <div className="text-xs text-[var(--bt-text-muted)] font-mono">
            EST. 2026 — TRADER-FIRST · ZERO NOISE
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <form
          onSubmit={onSubmit}
          data-testid="register-form"
          className="w-full max-w-md space-y-6 bg-[var(--bt-surface)] border border-[var(--bt-border)] rounded-sm p-8"
        >
          <div className="space-y-1.5">
            <p className="bt-label">Create account</p>
            <h2 className="font-heading text-3xl">Open your journal</h2>
            <p className="text-sm text-[var(--bt-text-muted)]">It only takes a minute.</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="name" className="bt-label">Full name</Label>
            <Input
              id="name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jane Trader"
              data-testid="register-name-input"
              className="bg-transparent border-[var(--bt-border)] focus-visible:border-[var(--bt-accent)] focus-visible:ring-1 focus-visible:ring-[var(--bt-accent)] h-11"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email" className="bt-label">Email</Label>
            <Input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              data-testid="register-email-input"
              className="bg-transparent border-[var(--bt-border)] focus-visible:border-[var(--bt-accent)] focus-visible:ring-1 focus-visible:ring-[var(--bt-accent)] h-11"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone" className="bt-label">
              Phone <span className="text-[var(--bt-text-muted)] normal-case tracking-normal">(optional — used for password reset)</span>
            </Label>
            <Input
              id="phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+91 98XXXXXXXX"
              data-testid="register-phone-input"
              className="bg-transparent border-[var(--bt-border)] focus-visible:border-[var(--bt-accent)] focus-visible:ring-1 focus-visible:ring-[var(--bt-accent)] h-11"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="bt-label">Password (min 8 chars)</Label>
            <PasswordInput
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="new-password"
              testId="register-password-input"
            />
          </div>

          {/* iter130 Phase A — DPDP consent. Required before backend
              will accept the registration. */}
          <label
            className="flex items-start gap-3 text-sm text-[var(--bt-text-muted)] leading-relaxed cursor-pointer select-none"
            data-testid="register-consent-label"
          >
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              data-testid="register-consent-checkbox"
              className="mt-1 w-4 h-4 accent-[var(--bt-accent)]"
            />
            <span>
              I agree to the{" "}
              <Link to="/terms" className="underline text-[var(--bt-text)] hover:text-[var(--bt-accent)]">
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link to="/privacy" className="underline text-[var(--bt-text)] hover:text-[var(--bt-accent)]">
                Privacy Policy
              </Link>
              .
            </span>
          </label>

          {retryNotice && (
            <div
              data-testid="register-retry-notice"
              className="text-sm text-[var(--bt-warn)] border border-[var(--bt-warn)]/40 bg-[var(--bt-warn)]/10 px-3 py-2 rounded-sm flex items-center gap-2"
            >
              <span className="inline-block w-2 h-2 rounded-full bg-[var(--bt-warn)] animate-pulse" />
              {retryNotice}
            </div>
          )}
          {error && (
            <div data-testid="register-error" className="text-sm text-[var(--bt-danger)] border border-[var(--bt-danger)]/40 bg-[var(--bt-danger)]/10 px-3 py-2 rounded-sm">
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={submitting}
            data-testid="register-submit-btn"
            className="w-full h-11 rounded-sm bg-[var(--bt-accent)] hover:bg-[var(--bt-accent-hover)] text-black font-bold tracking-wide"
          >
            {submitting ? "Creating account…" : (
              <>Create account <ArrowRight className="w-4 h-4 ml-1" /></>
            )}
          </Button>

          <p className="text-sm text-center text-[var(--bt-text-muted)]">
            Already have one?{" "}
            <Link to="/login" className="text-[var(--bt-accent)] hover:underline" data-testid="link-login">
              Sign in
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
